import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Select from 'react-select';

import { API } from '../../API';
import { Loader } from '../../components/Loader/Loader';
import Switch from '../../components/Switch';
import { Tabs } from '../../components/Tabs/Tabs';
import { useCurrentUser } from '../../contexts/CurrentUser';
import {
  EmbeddingMode,
  ExecutionMode,
  orderSchemaFields,
} from '../WindmillAppPage/types';
import {
  AdminAppDetail,
  CreateAppRequest,
  RefreshSchemaResponse,
  UpdateAppRequest,
} from './adminTypes';
import { AccessManager } from './components/AccessManager';
import { ExecutionsList } from './components/ExecutionsList';
import { FieldOrderEditor } from './components/FieldOrderEditor';

import styles from './WindmillAdminFormPage.module.scss';

type FormState = {
  name: string;
  description: string;
  slug: string;
  trigger_url: string;
  windmill_workspace: string;
  windmill_path: string;
  category: string;
  icon: string;
  default_execution_mode: ExecutionMode;
  result_display_type: 'json' | 'text' | 'table';
  is_active: boolean;
  embedding_modes: EmbeddingMode[];
};

const EMPTY_FORM: FormState = {
  name: '',
  description: '',
  slug: '',
  trigger_url: '',
  windmill_workspace: '',
  windmill_path: '',
  category: '',
  icon: '',
  default_execution_mode: 'sync',
  result_display_type: 'json',
  is_active: true,
  embedding_modes: [],
};

const slugify = (value: string): string =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);

export const WindmillAdminFormPage: React.FC = () => {
  const { t } = useTranslation();
  const { appId } = useParams<{ appId: string }>();
  const navigate = useNavigate();
  const [currentUser] = useCurrentUser();
  const isEditMode = Boolean(appId);

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [loaded, setLoaded] = useState(!isEditMode);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [app, setApp] = useState<AdminAppDetail | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [slugEdited, setSlugEdited] = useState(isEditMode);
  // The list shown in the editor: always a permutation of the current schema
  // keys, so it can never carry a duplicate or a name the schema dropped (the
  // PATCH rejects both).
  const [fieldOrder, setFieldOrder] = useState<string[]>([]);
  // What the PATCH should do with `field_order`. `undefined` means the admin
  // never touched the order, so the key is omitted and the stored value (very
  // often null) is left as it is — an app that was never deliberately ordered
  // must not gain an explicit order just because it was saved once. `null`
  // clears the ordering.
  const [fieldOrderChange, setFieldOrderChange] = useState<
    string[] | null | undefined
  >(undefined);

  useEffect(() => {
    if (!currentUser?.is_admin && !currentUser?.is_super_admin) {
      navigate('/');
      return;
    }
    if (!isEditMode) return;
    API.fetch('GET', `/API/v2/internal/windmill/apps/${appId}`)
      .then((data: AdminAppDetail) => {
        setApp(data);
        setFieldOrder(
          orderSchemaFields(data.input_schema?.properties, data.field_order),
        );
        setFieldOrderChange(undefined);
        setForm({
          name: data.name,
          description: data.description || '',
          slug: data.slug,
          trigger_url: data.trigger_url,
          windmill_workspace: data.windmill_workspace || '',
          windmill_path: data.windmill_path || '',
          category: data.category || '',
          icon: data.icon || '',
          default_execution_mode: data.default_execution_mode,
          result_display_type: data.result_display?.type || 'json',
          is_active: data.is_active,
          embedding_modes: data.embedding_modes ?? [],
        });
        setLoaded(true);
        setLoadError(null);
      })
      .catch((err: Error) => {
        setLoadError(err.message);
        setLoaded(true);
      });
  }, [appId, currentUser, isEditMode, navigate]);

  const handleNameChange = useCallback(
    (value: string) => {
      setForm((prev) => ({
        ...prev,
        name: value,
        slug: slugEdited ? prev.slug : slugify(value),
      }));
    },
    [slugEdited],
  );

  const handleSlugChange = useCallback((value: string) => {
    setSlugEdited(true);
    setForm((prev) => ({ ...prev, slug: value }));
  }, []);

  const handleFieldOrderChange = useCallback((next: string[]) => {
    setFieldOrder(next);
    setFieldOrderChange(next);
  }, []);

  // "Reset" means "no ordering", which the backend represents as null — not a
  // frozen snapshot of today's schema order.
  const handleFieldOrderReset = useCallback(() => {
    setFieldOrder(Object.keys(app?.input_schema?.properties || {}));
    setFieldOrderChange(null);
  }, [app]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setFormError(null);

      if (!form.name.trim()) {
        setFormError(t('Name is required'));
        return;
      }
      if (!isEditMode && !form.slug.trim()) {
        setFormError(t('Slug is required'));
        return;
      }
      if (!isEditMode && !form.trigger_url.trim()) {
        setFormError(t('Trigger URL is required'));
        return;
      }
      if (!form.windmill_workspace.trim()) {
        setFormError(t('Windmill workspace is required'));
        return;
      }
      if (!form.windmill_path.trim()) {
        setFormError(t('Windmill script path is required'));
        return;
      }

      setSubmitting(true);

      const resultDisplay = { type: form.result_display_type };

      if (isEditMode && appId) {
        const payload: UpdateAppRequest = {
          name: form.name,
          description: form.description || null,
          windmill_workspace: form.windmill_workspace || null,
          windmill_path: form.windmill_path || null,
          category: form.category || null,
          icon: form.icon || null,
          result_display: resultDisplay,
          is_active: form.is_active,
          default_execution_mode: form.default_execution_mode,
          embedding_modes: form.embedding_modes,
        };
        // Omit the key entirely when untouched: the PATCH is partial, so an
        // absent key leaves the stored order alone.
        if (fieldOrderChange !== undefined) {
          payload.field_order = fieldOrderChange;
        }
        API.fetch('PATCH', `/API/v2/internal/windmill/apps/${appId}`, null, payload)
          .then(() => {
            toast.success(t('App updated') as string);
            navigate('/windmill-admin');
          })
          .catch((err: Error) => {
            setFormError(err.message);
          })
          .finally(() => setSubmitting(false));
      } else {
        const payload: CreateAppRequest = {
          name: form.name,
          description: form.description || null,
          slug: form.slug,
          trigger_url: form.trigger_url,
          windmill_workspace: form.windmill_workspace || null,
          windmill_path: form.windmill_path || null,
          category: form.category || null,
          icon: form.icon || null,
          result_display: resultDisplay,
          default_execution_mode: form.default_execution_mode,
          embedding_modes: form.embedding_modes,
        };
        API.fetch('POST', '/API/v2/internal/windmill/apps', null, payload)
          .then((created: AdminAppDetail) => {
            toast.success(t('App created') as string);
            navigate(`/windmill-admin/${created.id}`);
          })
          .catch((err: Error) => {
            setFormError(err.message);
          })
          .finally(() => setSubmitting(false));
      }
    },
    [appId, fieldOrderChange, form, isEditMode, navigate, t],
  );

  const handleRefreshSchema = useCallback(() => {
    if (!appId) return;
    setRefreshing(true);
    API.fetch('POST', `/API/v2/internal/windmill/apps/${appId}/refresh-schema`)
      .then((resp: RefreshSchemaResponse) => {
        toast.success(
          t('Schema refreshed to v{{version}}', { version: resp.schema_version }) as string,
        );
        return API.fetch('GET', `/API/v2/internal/windmill/apps/${appId}`);
      })
      .then((data: AdminAppDetail | undefined) => {
        if (!data) return;
        setApp(data);
        setFieldOrder(
          orderSchemaFields(data.input_schema?.properties, data.field_order),
        );
        setFieldOrderChange(undefined);
      })
      .catch((err: Error) => {
        toast.error(err.message);
      })
      .finally(() => setRefreshing(false));
  }, [appId, t]);

  const modeOptions = useMemo(
    () => [
      { value: 'sync' as ExecutionMode, label: t('Synchronous (wait for result)') },
      { value: 'async' as ExecutionMode, label: t('Asynchronous (poll for result)') },
    ],
    [t],
  );

  const embeddingOptions = useMemo(
    () => [
      { value: 'case' as EmbeddingMode, label: t('Case page') },
      { value: 'message' as EmbeddingMode, label: t('Message card') },
    ],
    [t],
  );

  const resultTypeOptions = useMemo(
    () => [
      { value: 'json' as const, label: t('JSON viewer') },
      { value: 'text' as const, label: t('Plain text') },
      { value: 'table' as const, label: t('Table') },
    ],
    [t],
  );

  if (!loaded) return <Loader />;

  if (loadError) {
    return (
      <div className={styles.errorState}>
        <p>{loadError}</p>
        <Link to="/windmill-admin" className="button">
          {t('Back to list')}
        </Link>
      </div>
    );
  }

  const editForm = (
    <form onSubmit={handleSubmit} className={styles.form}>
      <div className={styles.grid}>
        <div className="form-element">
          <label>{t('Name')}*</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => handleNameChange(e.target.value)}
            required
          />
        </div>

        <div className="form-element">
          <label>{t('Slug')}*</label>
          <input
            type="text"
            value={form.slug}
            onChange={(e) => handleSlugChange(e.target.value)}
            required
            disabled={isEditMode}
          />
          {isEditMode && (
            <small className={styles.hint}>
              {t('Slug cannot be changed after creation.')}
            </small>
          )}
        </div>

        <div className={`form-element ${styles.fullWidth}`}>
          <label>{t('Description')}</label>
          <textarea
            rows={2}
            value={form.description}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, description: e.target.value }))
            }
          />
        </div>

        <div className={`form-element ${styles.fullWidth}`}>
          <label>{t('Trigger URL')}*</label>
          <input
            type="url"
            value={form.trigger_url}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, trigger_url: e.target.value }))
            }
            required
            disabled={isEditMode}
            placeholder="https://windmill.example.com/api/r/..."
          />
          {isEditMode && (
            <small className={styles.hint}>
              {t(
                'Trigger URL is immutable — create a new app to change the endpoint.',
              )}
            </small>
          )}
        </div>

        <div className="form-element">
          <label>{t('Windmill workspace')}*</label>
          <input
            type="text"
            value={form.windmill_workspace}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                windmill_workspace: e.target.value,
              }))
            }
            required
            placeholder="my-workspace"
          />
          <small className={styles.hint}>
            {t('Windmill workspace slug, e.g. "fde".')}
          </small>
        </div>

        <div className="form-element">
          <label>{t('Script path')}*</label>
          <input
            type="text"
            value={form.windmill_path}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, windmill_path: e.target.value }))
            }
            required
            placeholder="u/admin/my_script"
          />
          <small className={styles.hint}>
            {t('Path in Windmill, e.g. "u/admin/my_script" or "f/folder/name".')}
          </small>
        </div>

        <div className="form-element">
          <label>{t('Category')}</label>
          <input
            type="text"
            value={form.category}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, category: e.target.value }))
            }
            placeholder={t('Analytics, Moderation, ...')}
          />
        </div>

        <div className="form-element">
          <label>{t('Icon (emoji)')}</label>
          <input
            type="text"
            value={form.icon}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, icon: e.target.value }))
            }
            maxLength={4}
            placeholder="🪟"
          />
        </div>

        <div className="form-element">
          <label>{t('Execution mode')}</label>
          <Select
            className="chart-select"
            value={modeOptions.find(
              (o) => o.value === form.default_execution_mode,
            )}
            options={modeOptions}
            onChange={(opt) =>
              opt &&
              setForm((prev) => ({
                ...prev,
                default_execution_mode: opt.value,
              }))
            }
          />
        </div>

        <div className="form-element">
          <label>{t('Result display')}</label>
          <Select
            className="chart-select"
            value={resultTypeOptions.find(
              (o) => o.value === form.result_display_type,
            )}
            options={resultTypeOptions}
            onChange={(opt) =>
              opt &&
              setForm((prev) => ({ ...prev, result_display_type: opt.value }))
            }
          />
        </div>

        <div className={`form-element ${styles.fullWidth}`}>
          <label>{t('Embedding')}</label>
          <Select
            className="chart-select"
            isMulti
            placeholder={t('Not embedded')}
            value={embeddingOptions.filter((o) =>
              form.embedding_modes.includes(o.value),
            )}
            options={embeddingOptions}
            onChange={(opts) =>
              setForm((prev) => ({
                ...prev,
                embedding_modes: opts.map((o) => o.value),
              }))
            }
          />
          <small className={styles.hint}>
            {t(
              'Embedded apps are run from these placements instead of the Windmill Apps page. The script receives case_id, message_id and message_composite_id parameters.',
            )}
          </small>
        </div>

        {isEditMode && (
          <div className="form-element">
            <label>{t('Active')}</label>
            <Switch
              id="windmill-active-switch"
              value={form.is_active}
              onChange={(v: boolean) =>
                setForm((prev) => ({ ...prev, is_active: v }))
              }
            />
          </div>
        )}

        {isEditMode && app && (
          <div className={`form-element ${styles.fullWidth}`}>
            <label>{t('Field order')}</label>
            <small className={styles.hint}>
              {t('Fields are shown in this order on the app run form.')}
            </small>
            <FieldOrderEditor
              fieldOrder={fieldOrder}
              properties={app.input_schema?.properties || {}}
              onChange={handleFieldOrderChange}
              onReset={handleFieldOrderReset}
            />
          </div>
        )}
      </div>

      {formError && <div className={styles.formError}>{formError}</div>}

      <div className={styles.formActions}>
        <button
          type="button"
          className="btn-reset"
          onClick={() => navigate('/windmill-admin')}
        >
          {t('Cancel')}
        </button>
        <button type="submit" disabled={submitting} className="button">
          {submitting
            ? t('Saving...')
            : isEditMode
            ? t('Save changes')
            : t('Create app')}
        </button>
      </div>
    </form>
  );

  const editTab = (
    <div className={styles.tabContent}>
      {isEditMode && app && (
        <div className={styles.schemaInfo}>
          <div>
            <strong>{t('Schema version')}:</strong> v{app.schema_version}
            {app.schema_last_synced_at && (
              <span className={styles.syncedAt}>
                {' '}
                · {t('synced')}{' '}
                {new Date(app.schema_last_synced_at).toLocaleString()}
              </span>
            )}
          </div>
          <button
            type="button"
            className="button"
            onClick={handleRefreshSchema}
            disabled={refreshing}
          >
            <i className="las la-sync" />{' '}
            {refreshing ? t('Refreshing...') : t('Refresh schema')}
          </button>
        </div>
      )}
      {editForm}
      {isEditMode && app && (
        <details className={styles.rawBlock}>
          <summary>{t('View current input schema')}</summary>
          <pre>{JSON.stringify(app.input_schema, null, 2)}</pre>
        </details>
      )}
      {isEditMode && app?.injectable_fields && (
        <details className={styles.rawBlock}>
          <summary>{t('Injectable fields (server-side)')}</summary>
          <pre>{JSON.stringify(app.injectable_fields, null, 2)}</pre>
        </details>
      )}
    </div>
  );

  const tabs = isEditMode && appId
    ? [
        { title: t('Settings'), content: editTab },
        {
          title: t('Access'),
          content: <AccessManager appId={appId} />,
        },
        {
          title: t('Executions'),
          content: <ExecutionsList appId={appId} />,
        },
      ]
    : [{ title: t('Settings'), content: editTab }];

  return (
    <div className={styles.root}>
      <div className="page-header">
        <div className="breadcrumb">
          <span>
            <Link to="/windmill-admin">{t('Windmill Apps')}</Link>
          </span>
          <span>
            {isEditMode
              ? app?.name || t('Edit')
              : t('New app')}
          </span>
        </div>
      </div>

      <Tabs tabslist={tabs} heading="" switchMode={[]} activeMode={0} />
    </div>
  );
};
